// Ctrl/⌘ K command menu: jump to pages, find plans and exams, run actions.
import { languages, getLanguage, setLanguage, t } from "../core/i18n.js";
import { navItems } from "../core/config.js";
import { esc, groupLabel } from "../core/model.js";
import { planningGroups, plansIn, planTarget } from "../core/planning.js";
import { goToday, navigate, setPage } from "../core/navigation.js";
import { runtime } from "../core/runtime.js";
import { icon } from "./icons.js";
import { closeModal, openExamModal, openPlanModal } from "./plans.js";
import { openPlanningSetup } from "./planning-setup.js";
import { openReports } from "./reports.js";
import { isOwner } from "./owner.js";
import { pausePom, resetPom, startPom } from "./timers.js";
import { dataAction } from "./events.js";
import { openShortcutHelp, shortcutKeys } from "./shortcuts.js";

const RESULT_LIMIT = 8;
let state = null;

const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[‘’ʻʼ'`]/g, "");

export const isPaletteOpen = () =>
  !!document.querySelector("#modal.show .command-palette");

export function toggleCommandPalette() {
  if (isPaletteOpen()) closeModal();
  else openCommandPalette();
}

function commands() {
  const pages = navItems
    .filter(([id]) => id !== "owner" || isOwner())
    .map(([id, label]) => ({
      group: "Go to",
      label: t(label),
      keywords: label,
      icon: id === "today" ? "dashboard" : id,
      keys: shortcutKeys["page:" + id],
      run: () => setPage(id),
    }));
  const timerRunning = runtime.focusTimer?.running;
  const actions = [
    {
      label: t("Add plan"),
      keywords: "add plan new task create",
      icon: "plus",
      keys: shortcutKeys.add,
      run: () => openPlanModal(),
    },
    {
      label: t(timerRunning ? "Pause focus timer" : "Start focus timer"),
      keywords: "pomodoro focus timer start pause",
      icon: timerRunning ? "pause" : "play",
      keys: shortcutKeys.timer,
      run: () => (timerRunning ? pausePom() : startPom()),
    },
    {
      label: t("Reset focus timer"),
      keywords: "pomodoro reset timer",
      icon: "reset",
      keys: shortcutKeys.reset,
      run: resetPom,
    },
    {
      label: t("Go to today"),
      keywords: "today now",
      icon: "today",
      keys: shortcutKeys.today,
      run: goToday,
    },
    {
      label: t("Previous day"),
      keywords: "previous day back yesterday",
      icon: "arrow",
      flip: true,
      keys: shortcutKeys.prev,
      run: () => navigate(-1),
    },
    {
      label: t("Next day"),
      keywords: "next day forward tomorrow",
      icon: "arrow",
      keys: shortcutKeys.next,
      run: () => navigate(1),
    },
    {
      label: t("Add an exam"),
      keywords: "add exam date countdown",
      icon: "exams",
      run: () => openExamModal(),
    },
    {
      label: t("Plan this week"),
      keywords: "planning rhythm setup week",
      icon: "weekly",
      run: () => openPlanningSetup({}),
    },
    {
      label: t("Export data"),
      keywords: "export backup download json",
      icon: "arrow",
      run: () => dataAction("export"),
    },
    {
      label: t("Report an issue"),
      keywords: "report issue bug feedback",
      icon: "report",
      run: openReports,
    },
    {
      label: t("Keyboard shortcuts"),
      keywords: "keyboard shortcuts help hotkeys",
      icon: "spark",
      keys: shortcutKeys.help,
      run: openShortcutHelp,
    },
  ].map((item) => ({ ...item, group: "Actions" }));
  const preferences = [];
  for (const [id, label] of Object.entries(languages))
    preferences.push({
      group: "Preferences",
      label: `${t("Language")}: ${label}`,
      keywords: `language til язык ${id}`,
      icon: "globe",
      current: getLanguage() === id,
      run: () => setLanguage(id),
    });
  return [...actions, ...pages, ...preferences];
}

function contentResults(query) {
  if (!query) return [];
  const seen = new Set(),
    plans = [];
  const today = planTarget(runtime.selectedDate);
  for (const day of [today, ...planningGroups().map((g) => g.id)])
    for (const task of plansIn(day)) {
      if (seen.has(task.id)) continue;
      seen.add(task.id);
      plans.push({
        group: "Plans",
        label: task.name,
        hint: [task.schedule, groupLabel(day)].filter(Boolean).join(" · "),
        keywords: `${task.schedule || ""} ${task.notes || ""}`,
        icon: "check",
        run: () => openPlanModal(task, day),
      });
    }
  const exams = runtime.state.exams.map((exam) => ({
    group: "Exams",
    label: exam.name,
    hint: exam.date,
    keywords: exam.notes,
    icon: "exams",
    run: () => openExamModal(exam),
  }));
  return [...plans, ...exams];
}

function score(item, words) {
  const label = normalize(item.label),
    haystack = `${label} ${normalize(item.keywords)} ${normalize(item.hint)}`;
  if (!words.every((word) => haystack.includes(word))) return -1;
  if (label.startsWith(words.join(" "))) return 3;
  return words.every((word) => label.includes(word)) ? 2 : 1;
}

function results(query) {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (!words.length) return commands();
  const ranked = [...contentResults(query), ...commands()]
    .map((item) => ({ item, score: score(item, words) }))
    .filter((entry) => entry.score >= 0);
  const order = ["Plans", "Exams", "Actions", "Go to", "Preferences"];
  const groups = order.map((group) =>
    ranked
      .filter((entry) => entry.item.group === group)
      .sort((a, b) => b.score - a.score)
      .slice(0, RESULT_LIMIT)
      .map((entry) => entry.item),
  );
  return groups.flat();
}

const keyChips = (keys = []) =>
  keys.length
    ? `<span class="palette-keys">${keys.map((key) => `<kbd>${esc(key)}</kbd>`).join("")}</span>`
    : "";

function paintList() {
  const list = document.getElementById("paletteList");
  if (!list) return;
  if (!state.items.length) {
    list.innerHTML = `<div class="palette-empty">${icon("search")}<strong>${t("No results")}</strong><span>${t("Try a different word.")}</span></div>`;
    document
      .getElementById("paletteInput")
      .removeAttribute("aria-activedescendant");
    return;
  }
  let group = "";
  list.innerHTML = state.items
    .map((item, index) => {
      const heading =
        item.group !== group
          ? `<div class="palette-group" role="presentation">${t(item.group)}</div>`
          : "";
      group = item.group;
      return `${heading}<div class="palette-item" role="option" id="palette-${index}" data-index="${index}" aria-selected="${index === state.active}"><span class="palette-icon ${item.flip ? "flip" : ""}">${icon(item.icon)}</span><span class="palette-label">${esc(item.label)}${item.hint ? `<small>${esc(item.hint)}</small>` : ""}</span>${item.current ? `<span class="palette-current">${icon("check")}</span>` : ""}${keyChips(item.keys)}</div>`;
    })
    .join("");
  syncActive();
}

function syncActive() {
  const input = document.getElementById("paletteInput");
  document.querySelectorAll(".palette-item").forEach((el) => {
    el.setAttribute(
      "aria-selected",
      String(+el.dataset.index === state.active),
    );
  });
  const active = document.getElementById("palette-" + state.active);
  if (!active) return;
  input.setAttribute("aria-activedescendant", active.id);
  active.scrollIntoView({ block: "nearest" });
}

function run(index) {
  const item = state?.items[index];
  if (!item) return;
  state = null;
  closeModal();
  item.run();
}

export function openCommandPalette() {
  const host = document.getElementById("modal");
  host.innerHTML = `<div class="modal command-palette" role="dialog" aria-modal="true" aria-label="${t("Command menu")}">
    <div class="palette-search">${icon("search")}<input id="paletteInput" type="text" role="combobox" aria-expanded="true" aria-controls="paletteList" aria-autocomplete="list" autocomplete="off" spellcheck="false" placeholder="${t("Search plans, pages and actions…")}"><kbd>Esc</kbd></div>
    <div class="palette-list" id="paletteList" role="listbox" aria-label="${t("Results")}"></div>
    <div class="palette-foot"><span><kbd>↑</kbd><kbd>↓</kbd>${t("to navigate")}</span><span><kbd>↵</kbd>${t("to open")}</span><span><kbd>?</kbd>${t("Keyboard shortcuts")}</span></div>
  </div>`;
  host.classList.add("show");
  state = { items: results(""), active: 0 };
  paintList();
  const input = document.getElementById("paletteInput");
  input.focus();
  input.oninput = () => {
    state.items = results(input.value);
    state.active = 0;
    paintList();
    document.getElementById("paletteList").scrollTop = 0;
  };
  input.onkeydown = (event) => {
    const count = state.items.length;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!count) return;
      state.active =
        (state.active + (event.key === "ArrowDown" ? 1 : -1) + count) % count;
      syncActive();
    } else if (event.key === "Enter") {
      event.preventDefault();
      run(state.active);
    } else if (event.key === "?" && !input.value) {
      event.preventDefault();
      closeModal();
      openShortcutHelp();
    }
  };
  const list = document.getElementById("paletteList");
  list.onpointermove = (event) => {
    const option = event.target.closest(".palette-item");
    if (!option || +option.dataset.index === state.active) return;
    state.active = +option.dataset.index;
    syncActive();
  };
  list.onclick = (event) => {
    const option = event.target.closest(".palette-item");
    if (option) run(+option.dataset.index);
  };
}
